import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PencilIcon } from 'lucide-react'
import {
	Button,
	PropertyField, PropertyFieldGroup, PropertyFieldList,
	Spinner,
} from '@rentflowdev/ui'
import { useGetContactKycQuery } from '@/api'
import { formatPhone } from '@/lib/phone'
import { EditKycDialog } from '@/components/edit-kyc-dialog'
import type { ContactKyc } from '@/types'
import { DEFAULT_LOCALE } from '@/types'

interface TenantKycSectionProps {
	contactId: string
}

function formatDate(iso?: string): string | undefined {
	if (!iso) return undefined
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function buildFields(kyc: ContactKyc, t: (key: string) => string): { label: string; value: string }[] {
	const f = (key: string) => t(`tenants.kyc.fields.${key}`)
	const out: { label: string; value: string }[] = []
	const push = (label: string, value?: string) => {
		if (value && value.trim()) out.push({ label, value })
	}

	push(f('fullName'),         kyc.fullName)
	push(f('registrationAddr'), kyc.registrationAddr)
	push(f('phone'),            formatPhone(kyc.phone))
	push(f('inn'),              kyc.inn)

	if (kyc.tenantType === 'INDIVIDUAL') {
		push(f('passportSeries'),   kyc.passportSeries)
		push(f('passportNumber'),   kyc.passportNumber)
		push(f('passportIssuedBy'), kyc.passportIssuedBy)
	}

	if (kyc.tenantType === 'ENTREPRENEUR' || kyc.tenantType === 'COMPANY') {
		push(f('ogrn'), kyc.ogrn)
	}
	if (kyc.tenantType === 'COMPANY') {
		push(f('kpp'),            kyc.kpp)
		push(f('signatory'),      kyc.signatory)
		push(f('attorneyNumber'), kyc.attorneyNumber)
		push(f('attorneyFrom'),   formatDate(kyc.attorneyFrom))
		push(f('attorneyTo'),     formatDate(kyc.attorneyTo))
		push(f('representative'), kyc.representative)
	}

	push(f('bank'),        kyc.bank)
	push(f('bik'),         kyc.bik)
	push(f('bankAccount'), kyc.bankAccount)
	push(f('corrAccount'), kyc.corrAccount)

	return out
}

export function TenantKycSection({ contactId }: TenantKycSectionProps) {
	const { t } = useTranslation()
	const { data: kyc, isLoading } = useGetContactKycQuery(contactId)
	const [open, setOpen] = useState(false)

	if (isLoading) return <Spinner size="sm" />

	const fields = kyc ? buildFields(kyc, t) : []

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<span className="text-sm text-(--color-muted-foreground)">
					{kyc ? t(`tenants.kyc.types.${kyc.tenantType}`) : t('tenants.kyc.empty')}
				</span>
				<Button
					variant="outline"
					size="sm"
					iconLeft={<PencilIcon className="size-4" />}
					onClick={() => setOpen(true)}
				>
					{kyc ? t('tenants.kyc.edit') : t('tenants.kyc.fill')}
				</Button>
			</div>

			{kyc && fields.length > 0 && (
				<PropertyFieldGroup>
					<PropertyFieldList>
						{fields.map((f) => (
							<PropertyField key={f.label} label={f.label}>
								{f.value}
							</PropertyField>
						))}
					</PropertyFieldList>
				</PropertyFieldGroup>
			)}

			<EditKycDialog
				contactId={contactId}
				kyc={kyc ?? null}
				open={open}
				onOpenChange={setOpen}
			/>
		</div>
	)
}
